import {
  DEFAULT_SHOP_CONFIGURATION_B2B,
  type ShopConfiguration,
  type ShopConfigurationB2B,
} from '@@fire-b2b/types/ShopConfiguration'
import type { LocaleCode } from '@@fire-base/i18n'
import { deepMerge } from '@@fire-base/shared/utils/deepMerge'
import { defineStore } from 'pinia'

export type LoginFormVariant =
  | {
      type: 'standard'
      extended: boolean
      showCustomerNo: boolean
      showAcceptTos: boolean
    }
  | { type: 'sso-only' }

export type LoginFormType = LoginFormVariant['type']

export const useShopConfigStore = defineStore('shopConfig', () => {
  const shopConfig = ref<ShopConfiguration | null>(null)

  const b2bConfiguration = computed<ShopConfigurationB2B>(() => {
    const result = resultJsonParse<ShopConfigurationB2B>(shopConfig.value?.b2bConfiguration)
    if (isErr(result)) {
      logErr(result)
      return DEFAULT_SHOP_CONFIGURATION_B2B
    }
    return deepMerge(DEFAULT_SHOP_CONFIGURATION_B2B, result.data)
  })

  const companyName = computed(() => shopConfig.value?.name ?? '')
  const logo = computed(() => {
    return shopConfig.value?.logos?.[0] ?? undefined
  })

  const confirmToS = computed(() => {
    return !!b2bConfiguration.value.confirmToS
  })

  const selfServiceName = computed(() => {
    return b2bConfiguration.value.selfServiceName
  })

  const imprintContent = computed(() => shopConfig.value?.imprint || '')
  const termsOfServiceContent = computed(() => shopConfig.value?.termsOfService || '')
  const privacyPolicyContent = computed(() => shopConfig.value?.privacyPolicy || '')

  const copyrightName = computed(() => shopConfig.value?.copyright)

  const { fallbackLocaleCode } = useRuntimeConfig().public
  const activeLanguages = computed<LocaleCode[]>(() => {
    const languages = b2bConfiguration.value.general.languages
    // The fallback locale must always be active. The middleware redirects inactive locales
    // to fallbackLocaleCode — if that locale isn't itself active, this creates an infinite redirect loop.
    // This can happen when the ShopConfig is missing `general.languages` and the default (['en'])
    // doesn't match the deployment's fallback locale (e.g. 'de').
    if (!languages.includes(fallbackLocaleCode as LocaleCode)) {
      return [fallbackLocaleCode as LocaleCode, ...languages]
    }
    return languages
  })
  const isMultiLanguage = computed(() => activeLanguages.value.length > 1)

  function isLocaleActive(code: string): boolean {
    return activeLanguages.value.includes(code as LocaleCode)
  }

  function getGqlLocale(uiLocale: string): string {
    const fallback = b2bConfiguration.value.general.gqlLocaleFallback
    return fallback[uiLocale as LocaleCode] ?? uiLocale
  }

  const canRegister = computed(() => {
    return shopConfig.value?.registerForm ?? false
  })

  const loginLogo = computed(() => {
    return shopConfig.value?.loginLogos?.[0] ?? undefined
  })

  const loginImages = computed(() => {
    return (
      shopConfig.value?.loginImages?.map((image) => {
        return {
          id: image.id,
          image: image.thumbnail,
        }
      }) || []
    )
  })

  const selectedLoginFormType = ref<LoginFormType>('standard')
  const loginFormVariant = computed<LoginFormVariant>(() => {
    if (selectedLoginFormType.value === 'sso-only') {
      return {
        type: 'sso-only',
      }
    }

    const config = shopConfig.value
    const b2bConfig = b2bConfiguration.value
    const useSsoSaml = b2bConfig?.useSsoSaml ?? false
    const useSsoGoogle = b2bConfig?.useSsoGoogle ?? false
    const extended = useSsoSaml && useSsoGoogle
    const showCustomerNo = config?.loginWithCustomerNo ?? false
    const showAcceptTos = config?.loginAcceptTos ?? false

    return {
      type: 'standard',
      extended,
      showAcceptTos,
      showCustomerNo,
    }
  })

  async function loadConfig(): Promise<Result<ShopConfiguration | null>> {
    if (shopConfig.value) {
      return ok(shopConfig.value)
    }

    try {
      const { data } = await useFetch<ShopConfiguration>('/api/shop-configuration')

      shopConfig.value = data.value ?? null

      if (b2bConfiguration.value.useSsoSaml && !b2bConfiguration.value.useSsoGoogle) {
        selectedLoginFormType.value = 'sso-only'
      } else {
        selectedLoginFormType.value = 'standard'
      }

      return ok(shopConfig.value)
    } catch (err) {
      return errFromException(err)
    }
  }

  async function refresh() {
    shopConfig.value = null
    return await loadConfig()
  }

  return {
    b2bConfiguration,
    shopConfig,
    logo,
    companyName,
    loginLogo,
    loginImages,
    canRegister,
    selectedLoginFormType,
    loginFormVariant,
    confirmToS,
    imprintContent,
    termsOfServiceContent,
    privacyPolicyContent,
    copyrightName,
    selfServiceName,
    activeLanguages,
    isMultiLanguage,
    isLocaleActive,
    getGqlLocale,
    loadConfig,
    refresh,
  }
})
